import { Link } from 'react-router';
import { ROLE_LABELS, type PublicUser } from '@gitrader/shared';
import { Icon } from '../components/Icon';
import { IconButton } from '../components/ui';
import { useTheme, type ThemePreference } from '../lib/theme';
import { useHelp } from '../help/HelpProvider';
import { firstLeaf, type ResolvedRoute } from '../routes/helpers';
import type { RouteNode, TradingEnv } from '../routes/types';
import { EnvBadge } from './live/EnvBadge';
import { useSystemStatus, type WorkerState } from './systemStatus';

export interface TopBarProps {
  user: PublicUser;
  env: TradingEnv;
  deployment: string;
  chain: readonly RouteNode[];
  resolvedChain: readonly ResolvedRoute[];
  collapsed: boolean;
  onToggleRail: () => void;
  onOpenDrawer: () => void;
  onSignOut: () => void;
}

export function TopBar({
  user,
  env,
  deployment,
  chain,
  resolvedChain,
  collapsed,
  onToggleRail,
  onOpenDrawer,
  onSignOut,
}: TopBarProps) {
  return (
    <header
      data-topbar
      className="flex min-w-0 items-center gap-2 border-b border-border bg-surface px-3 pt-[env(safe-area-inset-top)] desk:px-3.5"
    >
      <div className="flex h-[52px] min-w-0 flex-1 items-center gap-2">
        <IconButton label="Open menu" onClick={onOpenDrawer} className="desk:hidden">
          <Icon name="menu" size={17} />
        </IconButton>
        <IconButton
          label={collapsed ? 'Expand navigation' : 'Collapse navigation'}
          aria-pressed={collapsed}
          onClick={onToggleRail}
          className="hidden desk:grid"
        >
          <Icon name="rail" size={17} />
        </IconButton>

        <Breadcrumb chain={chain} resolvedChain={resolvedChain} user={user} />

        <div className="flex-1" />

        <span className="desk:hidden">
          <EnvBadge env={env} deployment={deployment} compact />
        </span>
        <span className="hidden desk:inline">
          <EnvBadge env={env} deployment={deployment} />
        </span>

        <WorkerPill />
        <JobsPill />
        <HelpButton />
        <ThemeToggle />

        <span className="hidden text-right text-xs leading-tight sm:block">
          <span className="block text-ink">{user.displayName}</span>
          <span className="block text-[10.5px] text-ink-3">{ROLE_LABELS[user.role]}</span>
        </span>
        <IconButton label="Sign out" onClick={onSignOut}>
          <Icon name="signout" size={16} />
        </IconButton>
      </div>
    </header>
  );
}

/** Derived from the route tree: the full path, ancestors linking to their landing page. */
export function Breadcrumb({
  chain,
  resolvedChain,
  user,
}: {
  chain: readonly RouteNode[];
  resolvedChain: readonly ResolvedRoute[];
  user: PublicUser;
}) {
  const items = chain.length ? chain : [];
  return (
    <nav aria-label="Breadcrumb" className="min-w-0">
      <ol className="m-0 flex min-w-0 list-none items-center gap-1.5 p-0 text-[12.5px] text-ink-3">
        {items.length === 0 ? (
          <li aria-current="page" className="truncate font-semibold text-ink">
            Not found
          </li>
        ) : null}
        {items.map((node, i) => {
          const last = i === items.length - 1;
          const resolved = resolvedChain[i];
          const landing = resolved ? firstLeaf(resolved, user.role) : undefined;
          return (
            <li
              key={node.id}
              className={`min-w-0 items-center gap-1.5 ${last ? 'flex' : 'hidden sm:flex'}`}
            >
              {i > 0 ? (
                <span aria-hidden="true" className="hidden flex-none sm:inline">
                  /
                </span>
              ) : null}
              {last ? (
                <span aria-current="page" className="truncate font-semibold text-ink">
                  {node.label}
                </span>
              ) : (
                <Link
                  to={landing?.fullPath ?? '/'}
                  className="truncate hover:text-ink hover:underline"
                >
                  {node.label}
                </Link>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

const WORKER_LABEL: Record<WorkerState, string> = {
  unknown: 'Worker unknown',
  healthy: 'Worker healthy',
  stale: 'Worker stale',
  down: 'Worker down',
};
const WORKER_DOT: Record<WorkerState, string> = {
  unknown: 'bg-ink-3',
  healthy: 'bg-ok beat',
  stale: 'bg-warn',
  down: 'bg-crit',
};

/** Worker health, from every screen, clicking through to Jobs. Unknown is a real state. */
export function WorkerPill() {
  const { worker } = useSystemStatus();
  return (
    <Link
      to="/data/jobs"
      data-worker-state={worker.state}
      title={
        worker.lastSeen
          ? `${WORKER_LABEL[worker.state]} · last report ${worker.lastSeen}`
          : `${WORKER_LABEL[worker.state]} — no report received`
      }
      className="hidden h-[25px] items-center gap-1.5 rounded-full border border-border bg-raised px-2.5 text-[11.5px] font-semibold whitespace-nowrap text-ink hover:border-border-strong desk:inline-flex"
    >
      <span aria-hidden="true" className={`h-1.5 w-1.5 rounded-full ${WORKER_DOT[worker.state]}`} />
      {WORKER_LABEL[worker.state]}
    </Link>
  );
}

export function JobsPill() {
  const { jobs } = useSystemStatus();
  const known = jobs.running !== null;
  return (
    <Link
      to="/data/jobs"
      data-jobs-running={known ? String(jobs.running) : 'unknown'}
      title={
        known ? `${jobs.running} running jobs — open Jobs` : 'Running jobs unknown — open Jobs'
      }
      className="inline-flex h-[25px] items-center gap-1.5 rounded-full border border-border bg-raised px-2.5 text-[11.5px] font-semibold whitespace-nowrap text-ink hover:border-border-strong"
    >
      <span
        aria-hidden="true"
        className={`h-1.5 w-1.5 rounded-full ${known && jobs.running! > 0 ? 'bg-accent' : 'bg-ink-3'}`}
      />
      <span className="num">{known ? jobs.running : '—'}</span>
      <span className="hidden sm:inline">running</span>
    </Link>
  );
}

const THEME_ICON: Record<ThemePreference, 'monitor' | 'sun' | 'theme'> = {
  system: 'monitor',
  light: 'sun',
  dark: 'theme',
};

export function ThemeToggle() {
  const { preference, resolved, cycle } = useTheme();
  const label =
    preference === 'system'
      ? `Theme: system (${resolved}). Switch to light`
      : preference === 'light'
        ? 'Theme: light. Switch to dark'
        : 'Theme: dark. Switch to system';
  return (
    <IconButton label={label} data-theme-toggle={preference} onClick={cycle}>
      <Icon name={THEME_ICON[preference]} size={16} />
    </IconButton>
  );
}

/**
 * Opens the help panel at whatever page you are on.
 *
 * In the top bar rather than only on the pages themselves, so that "where do I find out what
 * this is" has one answer everywhere — including on a page that forgot to add its own.
 */
function HelpButton() {
  const { topic, toggle } = useHelp();
  return (
    <IconButton label="Help for this page" aria-expanded={topic !== null} onClick={toggle}>
      <Icon name="help" size={16} />
    </IconButton>
  );
}
